import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, FolderKanban, Search, Flag, Tag as TagIcon } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_META = {
  low:      { label: "Low",      cls: "bg-slate-500/15 text-slate-400 border-slate-500/30" },
  medium:   { label: "Medium",   cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  high:     { label: "High",     cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  critical: { label: "Critical", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
};
const TYPE_META = {
  task:               { label: "Task" },
  bug:                { label: "Bug" },
  cr:                 { label: "CR" },
  feature_request:    { label: "Feature Req" },
  improvement:        { label: "Improvement" },
  new_functionality:  { label: "New Func" },
  major_improvement:  { label: "Major Imp" },
  minor_improvement:  { label: "Minor Imp" },
};
const initialsOf = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");

function BacklogRow({ task, employees, labels, onOpen, isSortable }) {
  const sortable = useSortable({ id: task.id });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const assignee = employees.find((e) => e.id === task.assignee_id);
  const taskLabels = (task.label_ids || []).map((lid) => labels.find((l) => l.id === lid)).filter(Boolean);
  const pm = PRIORITY_META[task.priority] || PRIORITY_META.medium;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group glass rounded-lg px-3 py-2.5 flex items-center gap-3 hover:border-primary/40 transition"
      data-testid={`backlog-row-${task.id}`}
    >
      {isSortable && (
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
          title="Drag to reorder"
          data-testid={`backlog-drag-${task.id}`}
        >
          <GripVertical className="w-4 h-4" />
        </button>
      )}
      <button
        onClick={() => onOpen(task.id)}
        className="flex-1 min-w-0 text-left"
        data-testid={`backlog-open-${task.id}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Badge variant="outline" className="text-[10px] shrink-0">{(TYPE_META[task.type] || {}).label || task.type}</Badge>
          <span className="font-medium text-sm truncate">{task.title}</span>
        </div>
        {taskLabels.length > 0 && (
          <div className="flex items-center gap-1 mt-1.5 flex-wrap">
            {taskLabels.map((l) => (
              <span key={l.id} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md" style={{ backgroundColor: l.color + "22", color: l.color }}>
                <TagIcon className="w-2.5 h-2.5" />{l.name}
              </span>
            ))}
          </div>
        )}
      </button>
      <Badge variant="outline" className={`text-[10px] shrink-0 ${pm.cls}`}>
        <Flag className="w-2.5 h-2.5 mr-1" />{pm.label}
      </Badge>
      {task.estimated_hours ? (
        <span className="text-[11px] text-muted-foreground number shrink-0">{task.estimated_hours}h</span>
      ) : <span className="text-[11px] text-muted-foreground">—</span>}
      {assignee ? (
        <div className="flex items-center gap-1.5 shrink-0" title={assignee.full_name}>
          <Avatar className="h-6 w-6"><AvatarFallback className="text-[9px]">{initialsOf(assignee.full_name)}</AvatarFallback></Avatar>
        </div>
      ) : (
        <span className="text-[10px] text-muted-foreground shrink-0">Unassigned</span>
      )}
    </div>
  );
}

export default function Backlog() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [priority, setPriority] = useState("all");

  // Load projects + employees once
  useEffect(() => {
    api.get("/projects").then((r) => {
      setProjects(r.data);
      if (r.data.length > 0) setProjectId(r.data[0].id);
    });
    api.get("/employees").then((r) => setEmployees(r.data));
  }, []);

  // When project changes, load its backlog + labels
  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    Promise.all([
      api.get(`/tasks/backlog/list?project_id=${projectId}`).then((r) => setTasks(r.data)),
      api.get(`/projects/${projectId}/labels`).then((r) => setLabels(r.data)),
    ]).finally(() => setLoading(false));
  }, [projectId]);

  const filtered = useMemo(() => {
    return tasks.filter((t) => {
      if (priority !== "all" && t.priority !== priority) return false;
      if (q && !t.title.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [tasks, q, priority]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const canReorder = !q && priority === "all"; // only when the list isn't filtered

  const onDragEnd = async (ev) => {
    const { active, over } = ev;
    if (!over || active.id === over.id) return;
    const ids = tasks.map((t) => t.id);
    const from = ids.indexOf(active.id);
    const to = ids.indexOf(over.id);
    if (from < 0 || to < 0) return;
    const newList = arrayMove(tasks, from, to);
    setTasks(newList);  // optimistic
    const movedId = active.id;
    const before = newList[to + 1]?.id || null;
    const after = newList[to - 1]?.id || null;
    try {
      await api.patch(`/tasks/${movedId}/backlog-rank`, { before_task_id: before, after_task_id: after });
    } catch (e) {
      toast.error("Reorder failed");
      // reload truth
      api.get(`/tasks/backlog/list?project_id=${projectId}`).then((r) => setTasks(r.data));
    }
  };

  const stats = {
    total: tasks.length,
    high: tasks.filter((t) => t.priority === "high" || t.priority === "critical").length,
    unassigned: tasks.filter((t) => !t.assignee_id).length,
    hours: tasks.reduce((s, t) => s + (t.estimated_hours || 0), 0),
  };

  return (
    <div className="space-y-6">
      <PageHeader
        testid="backlog-header"
        title="Backlog"
        subtitle="Unassigned, unscheduled, not-yet-started tasks — ready to be pulled into a sprint"
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Tasks", value: stats.total, color: "text-primary" },
          { label: "High / Critical", value: stats.high, color: "text-amber-500" },
          { label: "Unassigned", value: stats.unassigned, color: "text-violet-500" },
          { label: "Est. Hours", value: stats.hours.toFixed(1), color: "text-emerald-500" },
        ].map((s) => (
          <div key={s.label} className="glass rounded-xl p-4 flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg bg-background/50 flex items-center justify-center ${s.color}`}>
              <FolderKanban className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</div>
              <div className="number font-medium text-xl leading-none mt-1">{s.value}</div>
            </div>
          </div>
        ))}
      </div>

      <SectionCard
        title="Backlog"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger className="h-9 w-56" data-testid="backlog-project"><SelectValue placeholder="Project" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} <span className="text-muted-foreground">· {p.code}</span></SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input className="h-9 pl-8 w-56" placeholder="Search title…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="backlog-search" />
            </div>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="h-9 w-40" data-testid="backlog-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All priorities</SelectItem>
                {Object.keys(PRIORITY_META).map((p) => <SelectItem key={p} value={p}>{PRIORITY_META[p].label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
      >
        {!canReorder && (
          <div className="text-[11px] text-amber-500 mb-2">
            Clear filters to drag-reorder the backlog.
          </div>
        )}
        {loading ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Loading backlog…</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-muted-foreground py-10 text-center">No tasks in the backlog match your filters.</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={filtered.map((t) => t.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5" data-testid="backlog-list">
                {filtered.map((t) => (
                  <BacklogRow
                    key={t.id} task={t}
                    employees={employees} labels={labels}
                    onOpen={(tid) => nav(`/tasks/${tid}`)}
                    isSortable={canReorder}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </SectionCard>
    </div>
  );
}
